import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import { createStateAndWarehouse } from "../../redux/thunk";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import toast from "react-hot-toast";

const PRIMARY_COLOR = "#008B50";
const PRIMARY_HOVER_COLOR = "#007A48";
const PRIMARY_RING_COLOR = "focus:ring-[#008B50]";
const PRIMARY_BORDER_COLOR = "focus:border-[#008B50]";

interface AddStateFormData {
  state: string;
  stateIcon: string;
  storeName: string;
  location: string;
  contactInfo: string;
}

export default function AddStateForm() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<AddStateFormData>({
    state: "",
    stateIcon: "",
    storeName: "",
    location: "",
    contactInfo: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const apiPayload = {
      state: formData.state,
      stateIcon: formData.stateIcon,
      wareHouse: [
        {
          storeName: formData.storeName,
          location: formData.location,
          contactInfo: formData.contactInfo,
        },
      ],
    };

    try {
      const resultAction = await dispatch(createStateAndWarehouse(apiPayload));
      if (createStateAndWarehouse.fulfilled.match(resultAction)) {
        toast.success("State and initial warehouse added successfully!");
        setTimeout(() => {
          setFormData({
            state: "",
            stateIcon: "",
            storeName: "",
            location: "",
            contactInfo: "",
          });
          navigate("/dashboard/warehouse-management");
        }, 2000);
      } else {
        const errorMsg =
          (resultAction.payload as string) || "Failed to add state.";
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">
        Add New State
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-md p-6 md:p-8 max-w-2xl mx-auto"
      >
        <div className="space-y-5">
          <div>
            <label
              htmlFor="state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State Name
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter State Name (e.g., Lagos)"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              required
            />
          </div>

          <div>
            <label
              htmlFor="stateIcon"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State Icon URL
            </label>
            <input
              type="url"
              id="stateIcon"
              name="stateIcon"
              value={formData.stateIcon}
              onChange={handleChange}
              placeholder="https://example.com/icon.png"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
            />
          </div>

          <hr className="my-4 border-t border-gray-200" />
          <p className="text-md font-medium text-gray-700 mb-1">
            Warehouse Details
          </p>

          <div>
            <label
              htmlFor="storeName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Warehouse/Store Name
            </label>
            <input
              type="text"
              id="storeName"
              name="storeName"
              value={formData.storeName}
              onChange={handleChange}
              placeholder="Enter Warehouse/Store Name"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              required
            />
          </div>

          <div>
            <label
              htmlFor="location"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Warehouse Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter Warehouse Location"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              required
            />
          </div>

          <div>
            <label
              htmlFor="contactInfo"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Warehouse Contact Info (Phone)
            </label>
            <input
              type="text"
              id="contactInfo"
              name="contactInfo"
              value={formData.contactInfo}
              onChange={handleChange}
              placeholder="Enter Contact Information"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              required
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 text-center text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
          >
            Discard
          </button>

          <button
            type="submit"
            disabled={loading}
            className={`inline-flex justify-center items-center px-5 py-2 text-sm font-medium text-white bg-[${PRIMARY_COLOR}] border border-transparent rounded-md shadow-sm hover:bg-[${PRIMARY_HOVER_COLOR}] focus:outline-none focus:ring-2 focus:ring-offset-2 ${PRIMARY_RING_COLOR} transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <AiOutlineLoading3Quarters className="animate-spin h-5 w-5" />
            ) : (
              "Create State"
            )}
          </button>
        </div>
      </form>

      {/* {success && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out"
          aria-labelledby="success-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg p-6 md:p-8 max-w-sm w-full text-center shadow-xl transform transition-all duration-300 ease-in-out scale-100">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <FaCheckCircle
                className="h-7 w-7 text-green-600"
                aria-hidden="true"
              />
            </div>
            <h2
              id="success-title"
              className="text-xl md:text-2xl font-semibold text-gray-800 mb-3"
            >
              State Created Successfully!
            </h2>
            <p className="text-sm text-gray-500">
              The new state and warehouse details have been saved.
            </p>
          </div>
        </div>
      )} */}
    </div>
  );
}
